import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import PropTypes from "prop-types";
import * as React from "react";
import { Influencer } from "../../../domain";
import { Button, FAB, Typography, AddIcon } from "../../../themeV2";
import { canBulkSelect, selectableIds } from "./bulkAccess";
import DragOverlayProduct from "./DragOverlayProduct";
import ShopBuilderProduct from "./ShopBuilderProduct";
import SortableProduct from "./SortableProduct";
import css from "./Products.module.css";

const List = React.memo(function List({
  emptyLabel,
  filter,
  influencer,
  isDragEnabled = false,
  isFiltered = false,
  isAdmin = false,
  isGroupShop = false,
  isSelectable = false,
  onClickAdd,
  onClose,
  onReorder,
  productFilter,
  refresh,
  salesCountMap = {},
  selection,
}) {
  const [activeProduct, setActiveProduct] = React.useState(null);
  const [activeWidth, setActiveWidth] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const filteredProducts = React.useMemo(() => {
    if (!influencer?.products?.length) return [];
    return influencer.products.filter(filter) ?? [];
  }, [influencer, filter]);

  const [localProducts, setLocalProducts] = React.useState(filteredProducts);

  React.useEffect(() => {
    setLocalProducts(filteredProducts);
  }, [filteredProducts]);

  const productIds = React.useMemo(
    () => localProducts.map((p) => p.productId),
    [localProducts],
  );

  const selectableProductIds = React.useMemo(
    () => selectableIds({ products: localProducts, isAdmin }),
    [localProducts, isAdmin],
  );

  const allVisibleSelected = React.useMemo(
    () =>
      isSelectable &&
      selectableProductIds.length > 0 &&
      selectableProductIds.every((id) => selection?.isSelected(id)),
    [isSelectable, selectableProductIds, selection],
  );

  const handleSelectAll = React.useCallback(() => {
    selection?.selectAll(selectableProductIds);
  }, [selection, selectableProductIds]);

  const handleDeselectAll = React.useCallback(() => {
    selection?.deselectAll(selectableProductIds);
  }, [selection, selectableProductIds]);

  const handleDragStart = React.useCallback(
    (event) => {
      const product = localProducts.find(
        (p) => p.productId === event.active.id,
      );
      setActiveProduct(product ?? null);
      const node = event.active?.rect?.current?.initial;
      setActiveWidth(node?.width ?? null);
    },
    [localProducts],
  );

  const handleDragEnd = React.useCallback(
    (event) => {
      setActiveProduct(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localProducts.findIndex(
        (p) => p.productId === active.id,
      );
      const overIndex = localProducts.findIndex((p) => p.productId === over.id);
      const draggedProduct = localProducts[oldIndex];

      if (oldIndex !== -1 && overIndex !== -1 && draggedProduct && onReorder) {
        setLocalProducts((prev) => arrayMove(prev, oldIndex, overIndex));
        onReorder({ product: draggedProduct, newPosition: overIndex + 1 });
      }
    },
    [localProducts, onReorder],
  );

  const handleDragCancel = React.useCallback(() => {
    setActiveProduct(null);
  }, []);

  if (!influencer) return null;

  if (!localProducts.length) {
    const emptyContent = isGroupShop ? (
      <div className={css.emptyState}>
        <p className={css.emptyLabel}>
          Products are managed in each individual shop
        </p>
      </div>
    ) : isFiltered ? (
      <div className={css.emptyState}>
        <p className={css.emptyLabel}>{emptyLabel}</p>
      </div>
    ) : (
      <div className={css.emptyState}>
        <AddIcon className={css.emptyIcon} />
        <Typography variant="title-md" as="p" className={css.emptyTitle}>
          No products yet
        </Typography>
        <Typography
          variant="body-lg"
          color="variant"
          as="p"
          className={css.emptyDescription}
        >
          Add your first product to start building your shop
        </Typography>
        {onClickAdd && (
          <FAB
            variant="extended"
            icon={<AddIcon />}
            label="Add Products"
            onClick={onClickAdd}
          />
        )}
      </div>
    );

    return (
      <>
        <div className={css.listHeader}>{productFilter}</div>
        {emptyContent}
      </>
    );
  }

  return (
    <>
      <div className={css.listHeader}>
        {productFilter}
        {isSelectable && selectableProductIds.length > 0 && (
          <div className={css.selectAllRow}>
            <Button
              variant="text"
              size="sm"
              disabled={!allVisibleSelected && selection?.isAtLimit}
              onClick={allVisibleSelected ? handleDeselectAll : handleSelectAll}
            >
              {allVisibleSelected ? "Deselect All Visible" : "Select All"}
            </Button>
          </div>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={productIds}
          strategy={rectSortingStrategy}
          disabled={!isDragEnabled}
        >
          <div className={css.productGrid}>
            {localProducts.map((product) => (
              <SortableProduct
                key={product.productId}
                id={product.productId}
                disabled={!isDragEnabled}
                isAtLimit={selection?.isAtLimit ?? false}
                isFeatured={product.isFeatured}
                isSelectable={isSelectable}
                isSelectionBlocked={!canBulkSelect({ product, isAdmin })}
                isSelected={selection?.isSelected(product.productId) ?? false}
                onToggleSelect={selection?.toggle}
                productName={product.name}
              >
                <ShopBuilderProduct
                  influencer={influencer}
                  onClose={onClose}
                  product={product}
                  refresh={refresh}
                  salesCount={salesCountMap[product.productId]}
                />
              </SortableProduct>
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          <DragOverlayProduct product={activeProduct} width={activeWidth} />
        </DragOverlay>
      </DndContext>
    </>
  );
});

List.propTypes = {
  emptyLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  filter: PropTypes.func,
  influencer: PropTypes.instanceOf(Influencer),
  isDragEnabled: PropTypes.bool,
  isFiltered: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isGroupShop: PropTypes.bool,
  isSelectable: PropTypes.bool,
  onClickAdd: PropTypes.func,
  onClose: PropTypes.func,
  onReorder: PropTypes.func,
  productFilter: PropTypes.node,
  refresh: PropTypes.func,
  salesCountMap: PropTypes.object,
  selection: PropTypes.shape({
    isSelected: PropTypes.func,
    toggle: PropTypes.func,
    selectAll: PropTypes.func,
    deselectAll: PropTypes.func,
    isAtLimit: PropTypes.bool,
  }),
};

export default List;
